import axios from "axios";

const fetchWithToken = async (url, method = "GET", data = null) => {
  const token = sessionStorage.getItem("accessToken") || "your-fallback-token";
  if (!token) {
    throw new Error("Access token not found");
  }

  const config = {
    method,
    url,
    headers: {
      Authorization: `Bearer ${token}`,
    },
  };

  if (!(data instanceof FormData)) {
    config.headers["Content-Type"] = "application/json";
  }
  if (data) {
    config.data = data;
  }

  try {
    const response = await axios(config);
    return response.data;
  } catch (error) {
    throw new Error(extractErrorMessage(error));
  }
};

const humanizeField = (field) =>
  field.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase());

// Shared by every "object of field -> message(s)" shape the API sends
// (`errors`, or `message`/`error` used as a field-error map).
const formatFieldErrorsObject = (obj) =>
  Object.entries(obj)
    .map(([field, messages]) => {
      const messageText = Array.isArray(messages) ? messages.join(', ') : messages;
      return `${humanizeField(field)}: ${messageText}`;
    })
    .join('\n');

// The validation-error shape: `{ error, details: [{ field, message }] }`.
const formatDetailsArray = (details) =>
  details
    .map((d) => (d.field ? `${humanizeField(d.field)}: ${d.message}` : d.message))
    .join('\n');

// The API isn't consistent about error shape across endpoints — sometimes a
// plain string headline (`{error: "News exist. Delete/move news first."}`),
// sometimes a headline plus per-field details
// (`{error: "Validation failed", details: [{field, message}]}`), and older
// endpoints use `message`/`errors` instead of `error`/`details`. Collect
// whichever pieces are present rather than assuming one fixed shape, so a
// real backend message never silently falls back to a generic string.
function extractErrorMessage(error) {
  const data = error.response?.data;
  if (!data) {
    return error.message || "Error fetching data";
  }

  const parts = [];

  if (typeof data.error === "string" && data.error.trim() !== "") {
    parts.push(data.error);
  } else if (typeof data.message === "string" && data.message.trim() !== "") {
    parts.push(data.message);
  }

  if (Array.isArray(data.details)) {
    parts.push(formatDetailsArray(data.details));
  } else if (data.errors && typeof data.errors === "object") {
    parts.push(formatFieldErrorsObject(data.errors));
  } else if (typeof data.message === "object" && data.message !== null) {
    parts.push(formatFieldErrorsObject(data.message));
  } else if (data.error && typeof data.error === "object") {
    parts.push(formatFieldErrorsObject(data.error));
  }

  return parts.length > 0 ? parts.join('\n') : "Error fetching data";
}

export default fetchWithToken;